// Bean names (§10.5, §21.7): the bot roster, a random-name generator and the player-name filter.
import { chance, pick, type RngHolder } from './rng';

export const NAME_MIN_LENGTH = 2;
export const NAME_MAX_LENGTH = 16;

/** Bot roster (§10.5). Gary is special-cased by the server and is not in this list. */
export const BOT_NAMES: readonly string[] = [
  'Grumbo', 'Lil Bonk', 'Mx. Noodle', 'Pudding', 'Sir Wobbleton', 'Beanzo', 'Tofu Tom', 'Captain Squish',
  'Jellybean Jr.', 'Mochi', 'Bumble', 'Wiggles', 'Dumpling', 'Professor Plop', 'Nugget', 'Biscuit',
  'Duchess Doink', 'Sprout', 'Pickles', 'Marshmallow', 'Waffles', 'Big Steve', 'Tiny Tina', 'Spudnik',
  'Gloop', 'Fizz', 'Sergeant Squash', 'Lady Lentil', 'Chickpea', 'Edamame', 'Mr. Butterbean', 'Pinto',
  'Baron von Bean', 'Gumdrop', 'Jiggles', 'Doug',
];
export const GARY_NAME = 'Gary';

// Random bean names: optional title + root syllable + ending, e.g. "Sir Squishkins".
const NAME_TITLES: readonly string[] = ['Sir', 'Lil', 'Big', 'Lady', 'Baron', 'Mx.', 'Dr.', 'Captain', 'Duchess'];
const NAME_ROOTS: readonly string[] = [
  'Wobble', 'Bonk', 'Squish', 'Jelly', 'Noodle', 'Pud', 'Mochi', 'Tofu', 'Bubble', 'Wiggle', 'Plop', 'Doink',
  'Snoot', 'Fizz', 'Gloop', 'Sprout', 'Bumble', 'Pickle', 'Waffle', 'Puddle', 'Splat', 'Boop', 'Gumbo',
  'Jiggle', 'Lentil', 'Mungo', 'Biscuit', 'Spud', 'Dumple', 'Squash',
];
const NAME_ENDINGS: readonly string[] = ['', 'o', 'y', 'zo', 'kins', 'bert', 'ster', 'ton', 'boop', 'ette', 'ling', 'wick', 'nik'];
const TITLE_CHANCE = 0.3;

/** Deterministic random bean name for a given RNG stream; always passes `cleanName`. */
export function randomBeanName(h: RngHolder): string {
  const root = pick(h, NAME_ROOTS) + pick(h, NAME_ENDINGS);
  if (chance(h, TITLE_CHANCE)) {
    const titled = `${pick(h, NAME_TITLES)} ${root}`;
    if (titled.length <= NAME_MAX_LENGTH) return titled;
  }
  return root;
}

/** Every name the generator can produce (used by tests to prove they pass the filter). */
export function allGeneratedNames(): string[] {
  const roots: string[] = [];
  for (const r of NAME_ROOTS) for (const e of NAME_ENDINGS) roots.push(r + e);
  const out = [...roots];
  for (const t of NAME_TITLES) {
    for (const r of roots) {
      const titled = `${t} ${r}`;
      if (titled.length <= NAME_MAX_LENGTH) out.push(titled);
    }
  }
  return out;
}

// Blocklists are matched against the folded form (see `foldForMatching`). Substring roots are
// long enough to rarely hit innocent names; short words are blocked only as whole words, so
// "Grape", "Spicy", "Glass" and "Tofukins" pass.
const BLOCKED_SUBSTRINGS: readonly string[] = [
  'fuck', 'shit', 'cunt', 'bitch', 'biatch', 'bastard', 'asshole', 'arsehole', 'dickhead',
  'cocksuck', 'motherf', 'whore', 'slut', 'skank', 'twat', 'wank', 'bollock', 'penis', 'vagina', 'dildo',
  'porn', 'jizz', 'semen', 'orgasm', 'blowjob', 'handjob', 'rapist', 'molest', 'pedophil', 'paedophil',
  'nigg', 'fagg', 'kike', 'chink', 'gook', 'wetback', 'beaner', 'tranny', 'retard', 'nazi', 'hitler',
  'pussy', 'titties',
];
const BLOCKED_WORDS: readonly string[] = [
  'ass', 'arse', 'fuk', 'fck', 'cum', 'fag', 'fags', 'nig', 'tit', 'tits', 'cock', 'dick', 'sex', 'spic', 'coon', 'anal',
  'rape', 'heil', 'kkk', 'dyke', 'homo', 'piss', 'crap', 'boob', 'boobs', 'nazis',
];

const LEET: Readonly<Record<string, string>> = {
  '0': 'o', '1': 'i', '3': 'e', '4': 'a', '5': 's', '7': 't', '8': 'b', '9': 'g', '@': 'a', '$': 's', '!': 'i', '+': 't',
};

/** Lowercase, accent-stripped, de-leeted words of a name (letters only). */
function foldForMatching(name: string): string[] {
  const base = name.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase();
  let out = '';
  for (const ch of base) out += LEET[ch] ?? ch;
  return out.split(/[^a-z]+/).filter((w) => w.length > 0);
}

export function isNameBlocked(name: string): boolean {
  const words = foldForMatching(name);
  const joined = words.join('');
  if (BLOCKED_SUBSTRINGS.some((root) => joined.includes(root))) return true;
  return words.some((w) => BLOCKED_WORDS.includes(w));
}

/**
 * Cleans a player-supplied name (§21.7): NFKC, strip control/format/zero-width characters,
 * collapse whitespace, trim, enforce 2–16 code points and the word list. Returns null when the
 * name must be replaced (the caller substitutes `randomBeanName`).
 */
export function cleanName(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const s = raw.normalize('NFKC').replace(/\s+/gu, ' ').replace(/\p{C}/gu, '').replace(/ {2,}/g, ' ').trim();
  const length = Array.from(s).length;
  if (length < NAME_MIN_LENGTH || length > NAME_MAX_LENGTH) return null;
  return isNameBlocked(s) ? null : s;
}
